import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ConversationDetailModal = ({ conversation, onClose }) => {
  const [activeTab, setActiveTab] = useState('transcript');

  if (!conversation) return null;

  const tabs = [
    { id: 'transcript', label: 'Transcript', icon: 'MessageSquare' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'sentiment', label: 'Sentiment', icon: 'Heart' },
    { id: 'recording', label: 'Recording', icon: 'Play' }
  ];

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col my-8">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <Image
              src={conversation?.customerAvatar}
              alt={conversation?.customerAvatarAlt}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">{conversation?.customerName}</h2>
              <p className="text-sm text-muted-foreground">{formatTimestamp(conversation?.timestamp)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-base"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 md:p-6 border-b border-border bg-muted/20">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Duration</p>
            <p className="text-sm font-semibold text-foreground">{formatDuration(conversation?.duration)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Agent</p>
            <p className="text-sm font-semibold text-foreground">{conversation?.agent}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Category</p>
            <p className="text-sm font-semibold text-foreground">{conversation?.category}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Satisfaction</p>
            <div className="flex items-center gap-1">
              <Icon name="Star" size={14} className="text-warning fill-warning" />
              <span className="text-sm font-semibold text-foreground">{conversation?.satisfaction}/10</span>
            </div>
          </div>
        </div>

        <div className="border-b border-border overflow-x-auto">
          <div className="flex items-center gap-2 p-4 md:p-6 min-w-max">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-base ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'transcript' && (
            <div className="space-y-4">
              {conversation?.transcript?.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message?.sender === 'customer' ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${
                    message?.sender === 'customer' ? 'bg-primary/10' : 'bg-secondary/10'
                  }`}>
                    <Icon
                      name={message?.sender === 'customer' ? 'User' : 'Bot'}
                      size={16}
                      className={message?.sender === 'customer' ? 'text-primary' : 'text-secondary'}
                    />
                  </div>
                  <div className={`flex-1 ${message?.sender === 'customer' ? 'text-left' : 'text-right'}`}>
                    <div className={`inline-block max-w-[80%] p-3 rounded-lg ${
                      message?.sender === 'customer' ?'bg-muted text-foreground' :'bg-primary text-primary-foreground'
                    }`}>
                      <p className="text-sm">{message?.text}</p>
                      <p className={`text-xs mt-1 ${
                        message?.sender === 'customer' ? 'text-muted-foreground' : 'text-primary-foreground/70'
                      }`}>
                        {message?.time}
                      </p>
                    </div>
                    {message?.sentiment && (
                      <div className="mt-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                          message?.sentiment === 'positive' ?'bg-success/10 text-success'
                            : message?.sentiment === 'negative' ?'bg-error/10 text-error' :'bg-muted text-muted-foreground'
                        }`}>
                          <Icon
                            name={message?.sentiment === 'positive' ? 'ThumbsUp' : message?.sentiment === 'negative' ? 'ThumbsDown' : 'Minus'}
                            size={10}
                          />
                          {message?.sentiment}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="MessageCircle" size={18} className="text-primary" />
                    <h4 className="text-sm font-semibold text-foreground">Message Statistics</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Total Messages</span>
                      <span className="text-sm font-medium text-foreground">24</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Customer Messages</span>
                      <span className="text-sm font-medium text-foreground">12</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Agent Messages</span>
                      <span className="text-sm font-medium text-foreground">12</span>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Clock" size={18} className="text-secondary" />
                    <h4 className="text-sm font-semibold text-foreground">Response Metrics</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Avg Response Time</span>
                      <span className="text-sm font-medium text-foreground">2.3s</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">First Response</span>
                      <span className="text-sm font-medium text-foreground">1.8s</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Resolution Time</span>
                      <span className="text-sm font-medium text-foreground">{formatDuration(conversation?.duration)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Hash" size={18} className="text-accent" />
                  <h4 className="text-sm font-semibold text-foreground">Key Topics Discussed</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {conversation?.keyTopics?.map((topic, index) => (
                    <span key={index} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sentiment' && (
            <div className="space-y-6">
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Heart" size={18} className="text-error" />
                  <h4 className="text-sm font-semibold text-foreground">Overall Sentiment Analysis</h4>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-2 rounded-full bg-success/10">
                      <Icon name="ThumbsUp" size={24} className="text-success" />
                    </div>
                    <p className="text-2xl font-semibold text-foreground">67%</p>
                    <p className="text-xs text-muted-foreground">Positive</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-2 rounded-full bg-muted">
                      <Icon name="Minus" size={24} className="text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-semibold text-foreground">25%</p>
                    <p className="text-xs text-muted-foreground">Neutral</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-2 rounded-full bg-error/10">
                      <Icon name="ThumbsDown" size={24} className="text-error" />
                    </div>
                    <p className="text-2xl font-semibold text-foreground">8%</p>
                    <p className="text-xs text-muted-foreground">Negative</p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">Sentiment Timeline</h4>
                <div className="space-y-2">
                  {conversation?.transcript?.map((message, index) => (
                    message?.sentiment && (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-16">{message?.time}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${
                              message?.sentiment === 'positive' ?'bg-success'
                                : message?.sentiment === 'negative' ?'bg-error' :'bg-muted-foreground'
                            }`}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <span className={`text-xs font-medium capitalize ${
                          message?.sentiment === 'positive' ?'text-success'
                            : message?.sentiment === 'negative' ?'text-error' :'text-muted-foreground'
                        }`}>
                          {message?.sentiment}
                        </span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'recording' && (
            <div className="space-y-6">
              <div className="bg-muted/30 rounded-lg p-6 text-center">
                <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10">
                  <Icon name="Play" size={32} className="text-primary" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Call Recording</h4>
                <p className="text-sm text-muted-foreground mb-6">Duration: {formatDuration(conversation?.duration)}</p>
                <div className="flex items-center justify-center gap-4">
                  <Button variant="default" iconName="Play" iconPosition="left">
                    Play Recording
                  </Button>
                  <Button variant="outline" iconName="Download" iconPosition="left">
                    Download
                  </Button>
                </div>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">Recording Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Format</span>
                    <span className="text-sm font-medium text-foreground">MP3</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Quality</span>
                    <span className="text-sm font-medium text-foreground">High (320kbps)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">File Size</span>
                    <span className="text-sm font-medium text-foreground">4.2 MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Recorded</span>
                    <span className="text-sm font-medium text-foreground">{formatTimestamp(conversation?.timestamp)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 md:p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="default" iconName="Download" iconPosition="left">
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetailModal;